import React, { useState, useEffect, useRef } from 'react';
import { User, ChevronDown, Search } from 'lucide-react';
import { useAuth } from '../../../context/AuthContext';

const AssigneeDropdown = ({
  taskId,
  currentAssigneeId,
  currentAssigneeName,
  onAssign,
  isUpdating = false,
  size = 'sm'
}) => {
  const { currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const searchInputRef = useRef(null);

  // Check if current user is admin (handle both role ID arrays and role objects)
  const isAdmin = currentUser?.roles && Array.isArray(currentUser.roles) &&
    (currentUser.roles.includes(1) || currentUser.roles.some(role => role.id === 1));

  useEffect(() => {
    if (isAdmin && isOpen && users.length === 0) {
      fetchUsers();
    }

    // Focus search input when dropdown opens
    if (isOpen && searchInputRef.current) {
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 100);
    }

    // Clear search when dropdown closes
    if (!isOpen) {
      setSearchTerm('');
    }
  }, [isAdmin, isOpen]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch('/api/project-tracker/users', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setUsers(data);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAssign = async (userId, userName) => {
    setIsOpen(false);
    if (onAssign) {
      await onAssign(taskId, userId, userName);
    }
  };

  // Filter users based on search term
  const filteredUsers = users.filter(user => {
    if (!searchTerm.trim()) return true;
    const searchLower = searchTerm.toLowerCase();
    const fullName = (user.full_name || '').toLowerCase();
    const username = (user.username || '').toLowerCase();
    return fullName.includes(searchLower) || username.includes(searchLower);
  });

  // Don't render dropdown if user is not admin
  if (!isAdmin) {
    // Show assignee info for non-admin users
    if (currentAssigneeName) {
      return (
        <div className={`inline-flex items-center space-x-1 text-slate-400 ${
          size === 'lg' ? 'text-sm' : 'text-xs'
        }`}>
          <User className={size === 'lg' ? 'w-4 h-4' : 'w-3 h-3'} />
          <span>{currentAssigneeName}</span>
        </div>
      );
    }
    return null;
  }

  const buttonClasses = `
    relative inline-flex items-center space-x-1 px-2 py-1 rounded text-xs font-medium transition-colors
    ${currentAssigneeId
      ? 'bg-blue-900/30 text-blue-300 border border-blue-800/50 hover:bg-blue-900/50'
      : 'bg-slate-700/50 text-slate-400 border border-slate-600/50 hover:bg-slate-700 hover:text-slate-300'
    }
    ${isUpdating ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
    ${size === 'lg' ? 'px-3 py-2 text-sm' : ''}
  `;

  return (
    <div className="relative">
      <button
        onClick={() => !isUpdating && setIsOpen(!isOpen)}
        disabled={isUpdating}
        className={buttonClasses}
        title={isAdmin ? "Click to assign task" : "Task assignee"}
      >
        <User className={size === 'lg' ? 'w-4 h-4' : 'w-3 h-3'} />
        <span>
          {isUpdating
            ? 'Updating...'
            : currentAssigneeName || 'Unassigned'
          }
        </span>
        {isAdmin && <ChevronDown className={size === 'lg' ? 'w-4 h-4' : 'w-3 h-3'} />}
      </button>

      {isOpen && (
        <div className="absolute top-full left-0 mt-1 w-64 bg-slate-800 border border-slate-700 rounded-lg shadow-lg z-50 max-h-80 flex flex-col">
          {loading ? (
            <div className="p-3 text-center text-slate-400">
              <div className="w-4 h-4 border-2 border-slate-400 border-t-transparent rounded-full animate-spin mx-auto"></div>
            </div>
          ) : (
            <>
              {/* Search bar */}
              <div className="p-3 border-b border-slate-700">
                <div className="relative">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" />
                  <input
                    ref={searchInputRef}
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Search users..."
                    className="w-full pl-9 pr-3 py-2 bg-slate-700 border border-slate-600 rounded text-slate-200 placeholder-slate-500 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 text-sm"
                  />
                </div>
              </div>

              {/* Scrollable content area */}
              <div className="flex-1 overflow-y-auto max-h-52">
                {/* Unassign option - always show if no search or matches "unassigned" */}
                {(!searchTerm.trim() || 'unassigned'.includes(searchTerm.toLowerCase())) && (
                  <button
                    onClick={() => handleAssign(null, null)}
                    className="w-full px-3 py-2 text-left text-sm text-slate-300 hover:bg-slate-700 flex items-center space-x-2"
                  >
                    <div className="w-6 h-6 bg-slate-600 rounded-full flex items-center justify-center">
                      <User className="w-3 h-3 text-slate-400" />
                    </div>
                    <span>Unassigned</span>
                  </button>
                )}

                {/* User list */}
                {filteredUsers.map((user) => (
                  <button
                    key={user.id}
                    onClick={() => handleAssign(user.id, user.full_name || user.username)}
                    className={`w-full px-3 py-2 text-left text-sm hover:bg-slate-700 flex items-center space-x-2 ${
                      user.id === currentAssigneeId
                        ? 'bg-blue-900/30 text-blue-300'
                        : 'text-slate-300'
                    }`}
                  >
                    <div className="w-6 h-6 bg-slate-600 rounded-full flex items-center justify-center">
                      <span className="text-xs font-medium text-white">
                        {(user.full_name || user.username).charAt(0).toUpperCase()}
                      </span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium truncate">
                        {user.full_name || user.username}
                      </div>
                      {user.full_name && user.username !== user.full_name && (
                        <div className="text-xs text-slate-500 truncate">
                          @{user.username}
                        </div>
                      )}
                    </div>
                  </button>
                ))}

                {/* No results message */}
                {searchTerm.trim() && filteredUsers.length === 0 && !'unassigned'.includes(searchTerm.toLowerCase()) && (
                  <div className="p-3 text-center text-slate-500 text-sm">
                    No users match "{searchTerm}"
                  </div>
                )}

                {/* No users available message */}
                {users.length === 0 && !loading && (
                  <div className="p-3 text-center text-slate-500 text-sm">
                    No users available
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      )}

      {/* Backdrop to close dropdown */}
      {isOpen && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => setIsOpen(false)}
        />
      )}
    </div>
  );
};

export default AssigneeDropdown;